/**
 * Cache Type Definitions
 */

import type { ItemCategory, Item, Blueprint, ItemBlueprint, IndustrySkill } from './api';

/**
 * Current cache version
 * Increment this when cache structure changes to invalidate old caches
 */
export const CACHE_VERSION = 1;

/**
 * Cache keys for different data types
 */
export enum CacheKey {
  CATEGORIES = 'categories',
  ITEMS = 'items',
  BLUEPRINTS = 'blueprints',
  ITEM_BLUEPRINTS = 'itemBlueprints',
  INDUSTRY_SKILLS = 'industrySkills',
  ITEM_PRICES = 'itemPrices',
  USER_SKILLS = 'userSkills',
  APP_SETTINGS = 'appSettings',
}

/**
 * Cache metadata for tracking cache state
 */
export interface CacheMetadata {
  key: string;
  version: number;
  timestamp: number; // Unix timestamp in milliseconds
  itemCount?: number;
  size?: number; // Approximate size in bytes
}

/**
 * Cached data wrapper with metadata
 */
export interface CachedData<T> {
  data: T;
  metadata: CacheMetadata;
}

/**
 * User skill levels (1-5 for each skill)
 */
export interface UserSkills {
  [skillName: string]: number; // Skill level (1-5)
}

/**
 * User's structure with configured ME bonus
 */
export interface UserStructure {
  structureId: string; // e.g., 'corp_citadel_i', 'assembly_workshop_iii'
  meBonus: number; // ME bonus percentage (e.g., 5 for -5%)
}

/**
 * Structure/Citadel bonus configuration
 * Stores array of structures user has available
 */
export interface StructureBonuses {
  structures: UserStructure[];
}

/**
 * Application settings
 */
export interface AppSettings {
  theme?: 'light' | 'dark';
  language?: string;
  autoRefreshCache?: boolean;
  cacheExpiryDays?: number;
}

/**
 * Cached API data structure
 */
export interface CachedApiData {
  categories: ItemCategory[];
  items: Item[];
  blueprints: Blueprint[];
  itemBlueprints: ItemBlueprint[];
  industrySkills: IndustrySkill[];
}

/**
 * Cache status information
 */
export interface CacheStatus {
  isPopulated: boolean;
  version: number;
  lastUpdated: number | null;
  itemCounts: {
    categories: number;
    items: number;
    blueprints: number;
    itemBlueprints: number;
    industrySkills: number;
  };
  isStale: boolean;
  isValid: boolean;
}

/**
 * Cache refresh progress
 */
export interface CacheRefreshProgress {
  step: string;
  progress: number; // 0-100
  message?: string;
}

/**
 * Image cache entry
 */
export interface CachedImage {
  url: string;
  blob: Blob;
  timestamp: number;
}

/**
 * Item price cache entry
 */
export interface ItemPriceCache {
  [itemId: string]: number; // itemId -> price (ISK)
}

/**
 * Cache expiry configuration
 */
export const CACHE_EXPIRY = {
  API_DATA: Infinity, // Never expires - user must manually refresh via "Refresh Data" button
  IMAGES: Infinity, // Never expires - images are static assets
  PRICES: 7 * 24 * 60 * 60 * 1000, // 7 days in milliseconds
};
